// Function index:
//   CharacterMountOpts — { worldId, worldName, partyId, partyName }.
//   showCharacterScreen(opts) — character picker / create / retire screen.
//
// Character creation/picker. Shown when bootstrap returns no character for
// the chosen party. Lets the user reuse an unassigned character, create a
// new one (assigned to the party in one call), or retire a character to
// free a tier slot (two-click arm — the admin end-combat pattern, no
// browser dialog; irreversible). Retired characters show read-only under a
// collapsed "Retired" section. The class field drives a live archetype
// preview and, for the 'adventurer' fallback, a required two-skill picker
// (server re-derives the archetype and validates independently — this is
// display/UX only). Resolves once a character is bound.

import { assignCharacter, createCharacter, myCharacters, retireCharacter, type CharacterRow } from '../api.js';
import { archetypeOf, ARCHETYPE_INFO, SKILL_NAMES } from '../game/archetypes.js';

export interface CharacterMountOpts {
  worldId: number;
  worldName: string;
  partyId: number;
  partyName: string;
}

export async function showCharacterScreen(opts: CharacterMountOpts): Promise<void> {
  const $ = (id: string): HTMLElement => document.getElementById(id)!;
  const screen = $('screen-character');
  screen.hidden = false;

  ($('character-sub') as HTMLParagraphElement).textContent =
    `Party "${opts.partyName}" in ${opts.worldName} needs a character before you can play.`;

  const error = $('character-error') as HTMLParagraphElement;
  const form = $('create-character-form') as HTMLFormElement;
  const existing = $('existing-chars');
  const list = $('character-list') as HTMLDivElement;

  // The screen owns its extra chrome (index.html stays untouched): a slot
  // counter above the create form, a collapsed Retired section below the list.
  const slotLine = document.createElement('p');
  slotLine.id = 'char-slots';
  slotLine.className = 'party-meta';
  form.parentElement!.insertBefore(slotLine, form);

  const retiredWrap = document.createElement('details');
  retiredWrap.id = 'retired-chars';
  const retiredSummary = document.createElement('summary');
  retiredSummary.textContent = 'Retired';
  const retiredList = document.createElement('div');
  retiredWrap.append(retiredSummary, retiredList);
  existing.parentElement!.appendChild(retiredWrap);

  let resolveFn!: () => void;
  const finish = (): void => { screen.hidden = true; resolveFn(); };

  const submitBtn = [...form.querySelectorAll('button')].find((b) => b.type === 'submit');

  const refresh = async (): Promise<void> => {
    const { characters, slots } = await myCharacters(opts.worldId);
    const living = characters.filter((c) => !c.retired);
    const reusable = living.filter((c) => c.assigned_party_id === null);
    const elsewhere = living.filter((c) => c.assigned_party_id !== null);
    const retired = characters.filter((c) => c.retired);

    const atCap = slots.used >= slots.cap;
    slotLine.textContent = `Characters: ${slots.used} of ${slots.cap}`
      + (atCap ? ' — retire a character or upgrade to create another' : '');
    slotLine.classList.toggle('error', atCap);
    if (submitBtn) submitBtn.disabled = atCap;

    existing.hidden = living.length === 0;
    ($('create-char-heading') as HTMLElement).textContent =
      reusable.length === 0 ? 'Create a new character' : 'Or create a new character';

    list.innerHTML = '';
    const handlers = {
      onPick: async (c: CharacterRow): Promise<void> => {
        try { await assignCharacter(c.id, opts.partyId); finish(); }
        catch (err) { error.textContent = (err as Error).message; }
      },
      onRetire: async (c: CharacterRow): Promise<void> => {
        try { await retireCharacter(c.id); await refresh(); }
        catch (err) { error.textContent = (err as Error).message; }
      },
    };
    for (const c of reusable) list.appendChild(row(c, true, handlers));
    for (const c of elsewhere) list.appendChild(row(c, false, handlers));

    retiredWrap.hidden = retired.length === 0;
    retiredList.innerHTML = '';
    for (const c of retired) {
      const line = document.createElement('div');
      line.className = 'party-meta';
      line.textContent = c.name;
      retiredList.appendChild(line);
    }
  };
  await refresh();

  // Live budget counter — mirrors the server's creation wall (3–18 per stat,
  // 78 points total) so nobody discovers the limit at submit time.
  const budgetEl = document.getElementById('stat-budget');
  const STAT_KEYS = ['str', 'dex', 'con', 'int', 'wis', 'cha'] as const;
  const updateBudget = (): void => {
    if (!budgetEl) return;
    const fd = new FormData(form);
    const total = STAT_KEYS.reduce((sum, k) => sum + (Number(fd.get(k)) || 0), 0);
    budgetEl.textContent = `3–18 per stat · ${total}/78 points spent`;
    budgetEl.classList.toggle('error', total > 78);
  };
  form.addEventListener('input', updateBudget);
  updateBudget();

  // Live archetype preview — mirrors the server's class-text derivation
  // (game/archetypes.ts) so the player sees what they'll get before
  // submitting. Adventurer (the no-match fallback) trades the package for
  // two free curated skill picks, chosen here.
  const classInput = form.querySelector<HTMLInputElement>('[name="class"]');
  const preview = document.createElement('p');
  preview.id = 'archetype-preview';
  preview.className = 'party-meta';
  const picker = document.createElement('div');
  picker.id = 'skill-picker';
  picker.hidden = true;
  const skillOptions = SKILL_NAMES.map((s) => `<option value="${s}">${s}</option>`).join('');
  picker.innerHTML = `
    <label>First skill <select id="pick-skill-1" name="pick_skill_1" required>${skillOptions}</select></label>
    <label>Second skill <select id="pick-skill-2" name="pick_skill_2" required>${skillOptions}</select></label>
  `;
  if (classInput) {
    const anchor = classInput.closest('label') ?? classInput;
    anchor.insertAdjacentElement('afterend', preview);
    preview.insertAdjacentElement('afterend', picker);
    const skill2 = picker.querySelector<HTMLSelectElement>('#pick-skill-2');
    if (skill2 && SKILL_NAMES.length > 1) skill2.value = SKILL_NAMES[1]!;

    const updatePreview = (): void => {
      const archetype = archetypeOf(classInput.value.trim() || null);
      const info = ARCHETYPE_INFO[archetype];
      const rest = [info.packageText, info.signature].filter(Boolean).join(' · ');
      preview.textContent = rest ? `→ fights as: ${info.label} — ${rest}` : `→ fights as: ${info.label}`;
      picker.hidden = archetype !== 'adventurer';
    };
    classInput.addEventListener('input', updatePreview);
    updatePreview();
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    const fd = new FormData(form);
    const klass = String(fd.get('class') ?? '').trim() || null;
    const stats: Record<string, number> = {};
    for (const k of STAT_KEYS) {
      const n = Number(fd.get(k));
      if (Number.isFinite(n)) stats[k] = Math.trunc(n);
    }
    const body: Parameters<typeof createCharacter>[0] = {
      world_id: opts.worldId,
      name: String(fd.get('name')),
      description: String(fd.get('description') ?? ''),
      class: klass,
      stats,
      party_id: opts.partyId,
    };
    if (archetypeOf(klass) === 'adventurer') {
      body.pick_skills = [String(fd.get('pick_skill_1') ?? ''), String(fd.get('pick_skill_2') ?? '')];
    }
    try {
      await createCharacter(body);
      finish();
    } catch (err) {
      error.textContent = (err as Error).message;
    }
  });

  return new Promise<void>((resolve) => { resolveFn = resolve; });
}

interface RowHandlers {
  onPick: (c: CharacterRow) => void;
  onRetire: (c: CharacterRow) => void;
}

function row(c: CharacterRow, usable: boolean, h: RowHandlers): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'party-row';
  const meta = usable
    ? `${esc(c.description.slice(0, 100))}${c.description.length > 100 ? '…' : ''}`
    : 'in another party';
  el.innerHTML = `
    <div>
      <div>${esc(c.name)}</div>
      <div class="party-meta">${meta}</div>
    </div>
    <div class="char-actions"></div>
  `;
  const actions = el.querySelector('.char-actions')!;
  if (usable) {
    const use = document.createElement('button');
    use.className = 'secondary';
    use.textContent = 'Use →';
    use.addEventListener('click', () => h.onPick(c));
    actions.appendChild(use);
  }
  // Two-click arm — no browser confirm() (the admin end-combat pattern).
  const retire = document.createElement('button');
  retire.className = 'secondary';
  retire.textContent = 'Retire';
  let armed = false;
  retire.addEventListener('click', () => {
    if (!armed) {
      armed = true;
      retire.textContent = 'Really retire? Forever';
      retire.classList.add('danger');
      return;
    }
    h.onRetire(c);
  });
  actions.appendChild(retire);
  return el;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
